import { api } from "./api.ts";

type GenerateFinanceReportRequest = {
  data: {
    report_type: string;
    options: {
      date_from?: string;
      date_to?: string;
      client_ids?: number[];
      file_ids?: number[];
      payment_status?: string;
      invoice_status?: string | string[];
      service_types?: string[];
      invoice_number?: string;
      client_service_map?: Record<
        string,
        { include?: string[] } | { exclude?: string[] }
      >;
      order_by?: string;
    };
  };
  customFilename?: string;
};

type GenerateFinanceReportResponse = {
  success?: boolean;
  filename?: string;
  message?: string;
  report_type?: string;
  options?: Record<string, unknown>;
  status?: string;
};

// Request shape for the async export endpoint mirrors the sync one;
// the response is small JSON instead of a binary blob because the
// XLSX is generated in the background and emailed.
type GenerateFinanceReportAsyncRequest = {
  data: GenerateFinanceReportRequest["data"];
};

// Returns a BackgroundJob id (the FE activity drawer polls
// /api/v1/background-jobs/ for the matching job_type).
type GenerateFinanceReportAsyncResponse = {
  background_job_id: number;
  status: "pending" | "in_progress" | "success" | "failed" | "error";
  message: string;
};

type ReportColumn = {
  key: string;
  name: string;
  type: string;
  hidden?: boolean; // optional flag to hide columns from user view
};

type GetReportDataRequest = {
  data: {
    report_type: string;
    options: {
      date_from?: string;
      date_to?: string;
      client_ids?: number[];
      file_ids?: number[];
      payment_status?: string;
      invoice_status?: string | string[];
      service_types?: string[];
      invoice_number?: string;
      client_service_map?: Record<
        string,
        { include?: string[] } | { exclude?: string[] }
      >;
      order_by?: string;
    };
  };
  // pagination parameters
  limit?: number;
  offset?: number;
};

type GetReportDataResponse = {
  success: boolean;
  report_type: string;
  data: Record<string, unknown>[];
  columns: ReportColumn[];
  total_records: number;
  options: Record<string, unknown>;
  meta?: Record<string, unknown>;
  // pagination fields
  count: number;
  next: string | null;
  previous: string | null;
  limit: number;
  offset: number;
};

type FinanceReport = {
  id: number;
  report_type: string;
  report_type_verbose: string;
  status?: string;
  status_verbose?: string;
  created: string;
  filename: string | null;
  download_url: string | null;
};

type GetFinanceReportsRequest = {
  filters?: Record<string, unknown>;
};

type GetFinanceReportsResponse = {
  results: FinanceReport[];
  count: number;
};

export const financeApi = api.injectEndpoints({
  endpoints: (build) => ({
    getFinanceReports: build.query<
      GetFinanceReportsResponse,
      GetFinanceReportsRequest
    >({
      query: ({ filters = {} }) => ({
        url: "api/v1/finance-reports/",
        method: "GET",
        params: filters,
      }),
      providesTags: ["FinanceReport"],
    }),
    generateFinanceReport: build.mutation<
      GenerateFinanceReportResponse,
      GenerateFinanceReportRequest
    >({
      query: ({ data, customFilename }) => ({
        url: "api/v1/generate-report/",
        method: "POST",
        body: data,
        responseHandler: (response) => {
          const contentType = response.headers.get("content-type");

          // if it's an Excel file, handle as blob for direct download
          if (
            contentType?.includes("spreadsheet") ||
            contentType?.includes("excel")
          ) {
            return response.blob().then((blob) => {
              // use custom filename if provided, otherwise fall back to server filename
              const serverFilename = response.headers
                .get("content-disposition")
                ?.match(/filename="([^"]+)"/)?.[1];
              const filename =
                customFilename || serverFilename || "finance_report.xlsx";

              // create blob and trigger download
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = filename;
              document.body.appendChild(a);
              a.click();
              window.URL.revokeObjectURL(url);
              document.body.removeChild(a);

              return { success: true, filename };
            });
          }

          // if it's a text file (LEDES export), handle as text for direct download
          if (contentType?.includes("text/plain")) {
            return response.text().then((text) => {
              // use custom filename if provided, otherwise fall back to server filename
              const serverFilename = response.headers
                .get("content-disposition")
                ?.match(/filename="([^"]+)"/)?.[1];
              const filename =
                customFilename || serverFilename || "finance_report.txt";

              // create blob and trigger download
              const blob = new Blob([text], { type: "text/plain" });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = filename;
              document.body.appendChild(a);
              a.click();
              window.URL.revokeObjectURL(url);
              document.body.removeChild(a);

              return { success: true, filename };
            });
          }

          // otherwise, handle as JSON
          return response.json();
        },
      }),
      invalidatesTags: ["FinanceReport"],
    }),
    generateFinanceReportAsync: build.mutation<
      GenerateFinanceReportAsyncResponse,
      GenerateFinanceReportAsyncRequest
    >({
      query: ({ data }) => ({
        url: "api/v1/generate-report-async/",
        method: "POST",
        body: data,
      }),
    }),
    getReportData: build.mutation<GetReportDataResponse, GetReportDataRequest>({
      query: ({ data, limit, offset }) => ({
        url: "api/v1/report-data/",
        method: "POST",
        body: data,
        params: {
          ...(limit !== undefined && { limit }),
          ...(offset !== undefined && { offset }),
        },
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetFinanceReportsQuery,
  useGenerateFinanceReportMutation,
  useGenerateFinanceReportAsyncMutation,
  useGetReportDataMutation,
} = financeApi;
